import _ from 'lodash';

import translateUtility from '../util/translate';
import colorMap from '../util/colorMap';

const TODO_CFD_COLORS = ['#00d4c2', '#6aeee7', '#00a193', '#00d4c2', '#00eed9'];
const CFD_COLORS = [
  '#ffd058',
  '#dd6bac',
  '#ffabae',
  '#aa5eaa',
  '#a7d2fc',
  '#fa7677',
  '#fee6e6',
  '#768efa',
  '#fac176',
  '#f76e2c',
  '#c98efb',
  '#eca7fc',
  '#a7fcd5',
  '#fa768e',
  '#f6fb8e',
  '#f79b2b',
  '#5ca3f9',
  '#f95c7c',
  '#a6fcea'
];
const DONE_CFD_COLORS = ['#0291bb', '#176697', '#389fe0', '#8fc9ed', '#295bbe'];

export default class ValuesService {
  constructor(dashboard, data) {
    this.dashboard = dashboard;
    this.cards = data.cards;
    this.labels = data.labels;
    this.members = data.members;
    this.activities = data.activities;
    this.customFields = data.customFields;
    this.app = data.app;

    let listsOrder = [].concat.apply([], [dashboard.todo, dashboard.wip, dashboard.done]);
    let orderedLists = _.map(data.lists, list => {
      let index = listsOrder.indexOf(list._id);
      list.order = index > -1 ? index : undefined;
      return list;
    });
    this.lists = orderedLists;
  }

  GetDefaultFilterLists(opt = {}) {
    let lists = this.dashboard.wip;

    if (!opt.excludeTodo) {
      lists = [].concat.apply([], [lists, this.dashboard.todo]);
    }

    if (!opt.excludeDone) {
      lists = [].concat.apply([], [lists, this.dashboard.done]);
    }

    let all = _(this.lists)
      .filter(l => lists.indexOf(l._id) > -1)
      .orderBy('order')
      .map(l => {
        let color, index;
        if (this.dashboard.todo.indexOf(l._id) > -1) {
          index = _.indexOf(this.dashboard.todo, l._id);
          color = TODO_CFD_COLORS[index % TODO_CFD_COLORS.length];
        } else if (this.dashboard.done.indexOf(l._id) > -1) {
          index = _.indexOf(this.dashboard.done, l._id);
          color = DONE_CFD_COLORS[index % DONE_CFD_COLORS.length];
        } else {
          index = _.indexOf(this.dashboard.wip, l._id);
          color = CFD_COLORS[index % CFD_COLORS.length];
        }

        return {
          label: l.name,
          color,
          value: {
            _id: l._id,
            order: l.order
          }
        };
      })
      .value();

    let selected = _(all)
      .filter(l => this.dashboard.todo.indexOf(l.value._id) < 0)
      .map(l => {
        return l.value;
      })
      .value();

    return {
      all,
      selected
    };
  }

  GetDefaultFilterLabels() {
    let shouldHaveNoLabel =
      !this.cards ||
      !this.cards.length ||
      _.find(this.cards, c => {
        return !c.labels || c.labels.length === 0;
      });

    if (this.app !== 'jira') {
      this.labels = _.orderBy(this.labels, 'name');
    }

    if (shouldHaveNoLabel && _.findIndex(this.labels, { extId: 'no-label' }) === -1) {
      this.labels.unshift({
        name: translateUtility.translate(`${this.app}.no-label`),
        extId: 'no-label',
        pos: 0,
        color: colorMap('default')
      });
    }

    let all = _(this.labels)
      .orderBy('pos')
      .map(l => {
        return {
          hidden: l.history,
          label: l.name,
          value: l.extId || l._id,
          color: l.color
        };
      })
      .value();

    return {
      all,
      selected: _.map(all, l => {
        return l.value;
      })
    };
  }

  GetDefaultFilterMembers() {
    let shouldHaveNoMember =
      this.members.length &&
      (!this.cards ||
        !this.cards.length ||
        _.find(this.cards, c => {
          return c.memberIDs.length === 0;
        }));

    this.members = _.orderBy(this.members, 'fullName');

    if (shouldHaveNoMember && _.findIndex(this.members, { _id: 'no-member' }) === -1) {
      this.members.unshift({
        fullName: translateUtility.translate(`${this.app}.no-member`),
        _id: 'no-member'
      });
    }

    let all = _.map(this.members, (l, i) => {
      return {
        label: l.fullName,
        value: l._id,
        image: l.image
      };
    });

    return {
      all,
      selected: _.map(all, l => {
        return l.value;
      })
    };
  }

  GetDefaultCustomFields() {
    const customFieldFilters = _.map(this.customFields, cf => {
      const shouldHaveNoOption = cf.name !== 'Status Category' || this.app !== 'jira';
      cf.options = _.orderBy(cf.options, 'value');
      if (shouldHaveNoOption) {
        if (_.findIndex(cf.options, { _id: 'no-option' }) === -1) {
          const value = cf.type === 'flag' ? 'false' : translateUtility.translate(`${this.app}.no-option`);
          cf.options.unshift({
            value,
            _id: 'no-option',
            color: colorMap('default')
          });
        } else {
          const from = _.findIndex(cf.options, { _id: 'no-option' });
          cf.options.splice(0, 0, cf.options.splice(from, 1)[0]);
        }
      }

      const all = _.map(cf.options, o => {
        return {
          hidden: o.history,
          label: o.value,
          value: o._id,
          color: o.color
        };
      });

      const selected = _.map(all, o => {
        return o.value;
      });

      return {
        extId: cf.extId || cf._id,
        name: cf.name,
        all,
        selected
      };
    });

    return customFieldFilters;
  }

  GetToDoItems(selectedLists = []) {
    let todoLists = _(selectedLists)
      .filter(l => _.indexOf(this.dashboard.todo, l._id) > -1)
      .map(l => {
        return l._id;
      })
      .value();

    if (!todoLists.length) {
      todoLists = this.dashboard.todo;
    }

    const todo = _.filter(this.cards, c => {
      if (!c.listId) {
        const lastActivity = _(this.activities)
          .filter({ cardId: c._id })
          .orderBy('startDate', 'desc')
          .last();

        return !!lastActivity ? _.indexOf(todoLists, lastActivity.listId) > -1 : false;
      }

      return _.indexOf(todoLists, c.listId) > -1;
    });

    return todo.length;
  }

  GetDoneStatuses() {
    let lists = this.dashboard.done;

    let all = _(this.lists)
      .filter(l => lists.indexOf(l._id) > -1)
      .orderBy('order')
      .map(l => {
        return {
          label: l.name,
          value: {
            _id: l._id,
            order: l.order
          }
        };
      })
      .value();

    let selected = [_.last(all)];

    return {
      all,
      selected
    };
  }
}
